import { useEffect, useState } from "react";
import * as rpc from "../../../common/jsonRpcRoutes";
import type { Booking } from "../../../common/jsonRpcTypes";
import { refusalMessage } from "../../utils/rpcRefusal";

export function useBooking(bookingId: string) {
  const [bookingData, setBookingData] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId) return;

    // Each booking starts from a clean slate, and a response that arrives after
    // the id has moved on is discarded, so neither the previous booking's
    // refusal nor its failure can outlive it.
    let current = true;
    setLoading(true);
    setBookingData(null);
    setError(null);
    setUnavailableMessage(null);

    rpc
      .call("/getBooking", { booking_id: bookingId })
      .then((data) => {
        if (!current) return;
        setBookingData(data);
        setLoading(false);
      })
      .catch((err) => {
        if (!current) return;
        // A booking the upstream refuses is a business outcome, not a fault:
        // its reason is surfaced separately so the page can show it in place of
        // an error, and retrying is not offered.
        const refusal = refusalMessage(err);
        setUnavailableMessage(refusal);
        setError(refusal ? null : err.message);
        setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [bookingId]);
  return { bookingData, loading, error, unavailableMessage };
}
